"use client";

import { Bus, Car, Hotel, MapPin, TrainFront } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const transportOptions = [{
  icon: "car",
  label: "Location Address",
  detail: "123 Botanical Way, Mossy Hollow. Nestled between the old oak grove and the river."
}, {
  icon: "train",
  label: "Hours of Operation",
  detail: "Tuesday – Sunday: 9:00 AM – 6:00 PM. Closed on Mondays for garden maintenance."
}, {
  icon: "bus",
  label: "Guided Tours",
  detail: "Daily botanical walks at 11:00 AM and 2:00 PM. Private group bookings available."
}];
const accommodations = [{
  name: "The Alabaster Suite",
  distance: "On-site garden cottage",
  code: "PETALSTAY"
}, {
  name: "Mossy Hollow Boutique Hotel",
  distance: "0.4 miles from garden",
  code: "GARDEN24"
}, {
  name: "Riverbed Eco-Lodge",
  distance: "1.5 miles from garden",
  code: ""
}];
export default function TravelAndStay() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="230862" id="visit-the-garden" ref={sectionRef} className="bg-background text-foreground py-20 px-4 md:px-8">
      {}
      <div className={`text-center mb-12 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <p className="text-sm uppercase tracking-widest mb-3 font-default" style={{
        color: "var(--primary)"
      }}>
          Plan Your Visit
        </p>
        <h2 className="font-default text-3xl md:text-5xl font-medium" style={{
        color: "var(--foreground)"
      }}>
          Visit the Garden
        </h2>
        <div className="mx-auto mt-4 w-12 h-px" style={{
        backgroundColor: "var(--border)"
      }} />
      </div>

      {}
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-md overflow-hidden shadow-md">
        {}
        <div ref={leftRef} className={`relative min-h-80 lg:min-h-auto transition-all duration-700 delay-100 ${leftVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`} style={{
        minHeight: "480px"
      }}>
          <iframe title="Venue Location Map" src="https://www.openstreetmap.org/export/embed.html?bbox=-73.9857%2C40.7484%2C-73.9757%2C40.7564&layer=mapnik&marker=40.7524%2C-73.9807" className="w-full h-full border-0" style={{
          minHeight: "480px",
          filter: "grayscale(1) contrast(1.1) brightness(0.95)"
        }} loading="lazy" />
          {}
          <div className="absolute bottom-6 left-6 flex items-center gap-2 rounded-md px-4 py-2 shadow-sm backdrop-blur-sm" style={{
          backgroundColor: "rgba(var(--background), 0.8)",
          border: "0.5px solid var(--border)"
        }}>
            <MapPin className="size-4 shrink-0" style={{
            color: "var(--primary)"
          }} />
            <div>
              <p className="text-xs font-default font-semibold" style={{
              color: "var(--foreground)"
            }}>
                PETAL Garden & Shop
              </p>
              <p className="text-xs font-default" style={{
              color: "var(--muted-foreground)"
            }}>
                123 Botanical Way, Mossy Hollow
              </p>
            </div>
          </div>
          {}
          <div className="absolute top-4 right-4 flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-default font-medium shadow-sm" style={{
          backgroundColor: "var(--primary)",
          color: "var(--primary-foreground)"
        }}>
            <MapPin className="size-3" />
            Our Sanctuary
          </div>
        </div>

        {}
        <div ref={rightRef} className={`flex flex-col gap-0 transition-all duration-700 delay-200 ${rightVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`} style={{
        backgroundColor: "var(--card)"
      }}>
          {}
          <Card className="border-0 border-b gap-4 py-8" style={{
          borderColor: "var(--border)",
          backgroundColor: "var(--card)"
        }}>
            <CardHeader className="pb-0">
              <CardTitle className="font-default text-xl font-medium flex items-center gap-2" style={{
              color: "var(--foreground)"
            }}>
                <Car className="size-5" style={{
                color: "var(--primary)"
              }} />
                Garden Details
              </CardTitle>
              <p className="text-sm font-default" style={{
              color: "var(--muted-foreground)"
            }}>
                Essential information for your arrival
              </p>
            </CardHeader>
            <CardContent className="pt-0">
              <ul className="space-y-4">
                {transportOptions.map((opt, i) => <li data-index={i} key={i} className="flex gap-3">
                    <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md" style={{
                  backgroundColor: "var(--muted)"
                }}>
                      {opt.icon === "car" && <MapPin className="size-4" style={{
                    color: "var(--primary)"
                  }} />}
                      {opt.icon === "train" && <TrainFront className="size-4" style={{
                    color: "var(--primary)"
                  }} />}
                      {opt.icon === "bus" && <Bus className="size-4" style={{
                    color: "var(--primary)"
                  }} />}
                    </div>
                    <div>
                      <p className="text-sm font-default font-semibold" style={{
                    color: "var(--foreground)"
                  }}>
                        {opt.label}
                      </p>
                      <p className="text-sm font-default leading-relaxed" style={{
                    color: "var(--muted-foreground)"
                  }}>
                        {opt.detail}
                      </p>
                    </div>
                  </li>)}
              </ul>
            </CardContent>
          </Card>

          {}
          <Card className="border-0 gap-4 py-8 flex-1" style={{
          backgroundColor: "var(--card)"
        }}>
            <CardHeader className="pb-0">
              <CardTitle className="font-default text-xl font-medium flex items-center gap-2" style={{
              color: "var(--foreground)"
            }}>
                <Hotel className="size-5" style={{
                color: "var(--primary)"
              }} />
                Stay With Us
              </CardTitle>
              <p className="text-sm font-default" style={{
              color: "var(--muted-foreground)"
            }}>
                Curated retreats within walking distance
              </p>
            </CardHeader>
            <CardContent className="pt-0">
              <ul className="space-y-4 mb-6">
                {accommodations.map((hotel, i) => <li data-index={i} key={i} className="flex flex-col gap-0.5 rounded-md px-4 py-3 border" style={{
                borderColor: "var(--border)",
                backgroundColor: "var(--accent)"
              }}>
                    <p className="text-sm font-default font-semibold" style={{
                  color: "var(--foreground)"
                }}>
                      {hotel.name}
                    </p>
                    <p className="text-xs font-default" style={{
                  color: "var(--muted-foreground)"
                }}>
                      {hotel.distance}
                    </p>
                    {hotel.code ? <p className="text-xs font-default mt-1" style={{
                  color: "var(--primary)"
                }}>
                        Use code: <span className="font-semibold">{hotel.code}</span> for a botanical discount
                      </p> : null}
                  </li>)}
              </ul>
              <Button asChild className="w-full font-default font-medium tracking-wide rounded-md transition-colors duration-300" style={{
              backgroundColor: "var(--accent-foreground)",
              color: "var(--background)"
            }}>
                <Link to="/TheGardenPage">Book a Tour</Link>
              </Button>
              <p className="text-center text-xs font-default mt-3" style={{
              color: "var(--muted-foreground)"
            }}>
                Mention &quot;PETAL GUEST&quot; when booking for preferred rates
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}